import React, { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCatalogue } from "../../../../hooks/useCatalogue";

function Dot({ registered }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 11,
        height: 11,
        borderRadius: "50%",
        background: registered ? "#22c55e" : "transparent",
        border: registered ? "none" : "2px solid #cbd5e1",
        verticalAlign: "middle",
      }}
      title={registered ? "Registered" : "Not registered"}
    />
  );
}

function Table({ students = [] }) {
  const { level, semester } = useParams();
  const navigate = useNavigate();
  const { getCoursesForLevel } = useCatalogue();

  const { sortedStudents, course_codes, external_codes } = useMemo(() => {
    const sortedStudents = [...students].sort((a, b) => {
      const nameA = (a?.fullname || "").toLowerCase();
      const nameB = (b?.fullname || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // Professional course columns — strictly from catalogue for this level/semester only
    const current_semester = getCoursesForLevel(level, semester);
    const course_codes = Object.keys(current_semester);

    // External course columns — collect unique codes across all students
    const externalCodesSet = new Set();
    sortedStudents.forEach((student) => {
      (student?.courses || []).forEach((c) => {
        if (c.external) externalCodesSet.add(c.course_code?.toLowerCase());
      });
    });
    const external_codes = [...externalCodesSet];

    return { sortedStudents, course_codes, external_codes };
  }, [students, level, semester, getCoursesForLevel]);

  const handleStudentClick = useCallback(
    (studentId) => {
      navigate(`/admin/student/${studentId}`);
    },
    [navigate]
  );

  const allCols = [...course_codes, ...external_codes];

  return (
    <div className="level_table_wrap">
      {/* legend */}
      <div className="level_dot_legend">
        <span className="level_legend_item">
          <span className="level_dot level_dot--on" />
          Registered
        </span>
        <span className="level_legend_item">
          <span className="level_dot level_dot--off" />
          Not registered
        </span>
      </div>

      <div className="level_table_scroll">
        <table className="level_main_table">
          <thead>
            <tr>
              <th className="center">S/N</th>
              <th>Reg. No.</th>
              <th>Names</th>
              {course_codes.map((code) => (
                <th key={code} className="center">
                  {code}
                </th>
              ))}
              {external_codes.length > 0 && (
                <th
                  className="center level_ext_header"
                  colSpan={external_codes.length}
                >
                  External courses
                </th>
              )}
            </tr>
            {external_codes.length > 0 && (
              <tr className="level_subheader_row">
                <th colSpan={3 + course_codes.length} />
                {external_codes.map((code) => (
                  <th key={code} className="center">
                    {code}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {sortedStudents.map((student, i) => (
              <tr key={student?.student_id || i}>
                <td className="center">{i + 1}</td>
                <td
                  className="level_clickable_cell"
                  onClick={() => handleStudentClick(student?.student_id)}
                >
                  {student?.reg_no || "N/A"}
                </td>
                <td
                  className="level_clickable_cell level_name_cell"
                  onClick={() => handleStudentClick(student?.student_id)}
                >
                  {student?.fullname || "Unknown"}
                </td>
                {allCols.map((code) => {
                  const registered = !!(student?.courses || []).find(
                    (c) => c.course_code?.toLowerCase() === code?.toLowerCase()
                  );
                  return (
                    <td key={code} className="center">
                      <Dot registered={registered} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(Table);
